import { compactSlug, padIbge } from "./concessionSheet";
import { padCep8 } from "./cepRangeAudit";

export function mergeCepIntervals(intervals) {
  const list = (intervals || [])
    .map((x) => ({
      start: Number(x.start ?? x.start_cep),
      end: Number(x.end ?? x.end_cep),
    }))
    .filter((x) => Number.isFinite(x.start) && Number.isFinite(x.end) && x.end >= x.start)
    .sort((a, b) => a.start - b.start || a.end - b.end);

  const out = [];
  for (const cur of list) {
    const last = out[out.length - 1];
    if (last && cur.start <= last.end + 1) {
      last.end = Math.max(last.end, cur.end);
    } else {
      out.push({ start: cur.start, end: cur.end });
    }
  }
  return out;
}

/** Mesmo formato do exemplo-faixas-cep.csv (CEP Inicial;CEP Final). */
export function cepRangesToExampleCsv(intervals) {
  const merged = mergeCepIntervals(intervals);
  const lines = ["CEP Inicial;CEP Final"];
  for (const r of merged) {
    lines.push(`${padCep8(r.start)};${padCep8(r.end)}`);
  }
  return `${lines.join("\r\n")}\r\n`;
}

function indexMunByIbge(municipalityRanges) {
  const map = new Map();
  for (const m of municipalityRanges || []) {
    const ibge = padIbge(m.ibge);
    if (!ibge) continue;
    const start = Number(m.start ?? m.start_cep);
    const end = Number(m.end ?? m.end_cep);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) continue;
    if (!map.has(ibge)) map.set(ibge, []);
    map.get(ibge).push({ start, end });
  }
  return map;
}

function fileSlug(label, fallback) {
  return compactSlug(label) || fallback;
}

/**
 * Gera um CSV por concessionária e um por filial, a partir das cidades da planilha + faixas IBGE.
 */
export function buildConcessionCepExport({
  sheetRows = [],
  dealerMap = {},
  districtMap = {},
  municipalityRanges = [],
} = {}) {
  const munByIbge = indexMunByIbge(municipalityRanges);
  const dealerAcc = new Map();
  const districtAcc = new Map();
  let missingIbge = 0;
  let matchedCities = 0;

  for (const row of sheetRows || []) {
    const ibge = padIbge(row.ibge);
    const spans = ibge ? munByIbge.get(ibge) : null;
    if (!spans?.length) {
      missingIbge += 1;
      continue;
    }
    matchedCities += 1;
    const dealer = row.dealer || "sem-marca";
    if (!dealerAcc.has(dealer)) {
      dealerAcc.set(dealer, { dealer, region: dealerMap[row.dealer] || "", spans: [], cities: 0 });
    }
    const d = dealerAcc.get(dealer);
    d.spans.push(...spans);
    d.cities += 1;

    const district = row.district || dealer;
    if (!districtAcc.has(district)) {
      districtAcc.set(district, {
        dealer,
        district,
        region: dealerMap[row.dealer] || "",
        target: districtMap[row.district] || "",
        spans: [],
        cities: 0,
      });
    }
    const f = districtAcc.get(district);
    f.spans.push(...spans);
    f.cities += 1;
  }

  const dealers = [...dealerAcc.values()]
    .map((item) => {
      const merged = mergeCepIntervals(item.spans);
      return {
        dealer: item.dealer,
        region: item.region,
        cities: item.cities,
        rangeCount: merged.length,
        fileName: `faixas-cep-${fileSlug(item.dealer, "concessionaria")}.csv`,
        csv: cepRangesToExampleCsv(merged),
      };
    })
    .sort((a, b) => a.dealer.localeCompare(b.dealer, "pt"));

  const districts = [...districtAcc.values()]
    .map((item) => {
      const merged = mergeCepIntervals(item.spans);
      const slug = [fileSlug(item.dealer, "marca"), fileSlug(item.district, "filial")].join("-");
      return {
        dealer: item.dealer,
        district: item.district,
        region: item.region,
        target: item.target,
        cities: item.cities,
        rangeCount: merged.length,
        fileName: `faixas-cep-${slug}.csv`,
        csv: cepRangesToExampleCsv(merged),
      };
    })
    .sort((a, b) => a.dealer.localeCompare(b.dealer, "pt") || a.district.localeCompare(b.district, "pt"));

  return {
    dealers,
    districts,
    missingIbge,
    matchedCities,
    summary: {
      dealers: dealers.length,
      districts: districts.length,
      missingIbge,
      matchedCities,
    },
  };
}

export function downloadTextFile(filename, text, mime = "text/csv;charset=utf-8") {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
